import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// routes
import { PATH_PAGE } from '../../routes/paths';
// components
import Iconify from '../../components/Iconify';
import Scrollbar from '../../components/Scrollbar';
import Image from '../../components/Image';
// hooks
import useLanguage from '../../hooks/useLanguage';
import useLocales from '../../hooks/useLocales';
// utils
import { fCentsToCoins } from '../../utils/formatNumber';
// actions
import * as actionsCart from '../../redux/shop/cart/actions';

// ----------------------------------------------------------------------

const CartButton = styled(IconButton)(({ theme, isOffset }) => ({
  padding: theme.spacing(1),
  marginLeft: theme.spacing(1),
  color: isOffset ? theme.palette.text.primary : theme.palette.common.white,
  '&:hover': {
    backgroundColor: isOffset
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.common.white, 0.16),
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 2,
    top: 2,
    padding: '0 4px',
    minWidth: 18,
    height: 18,
    fontSize: '0.7rem',
    fontWeight: 700,
  },
}));

const EmptyCartBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const CartItemImage = styled(Image)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: theme.spacing(1),
  flexShrink: 0,
}));

const CheckoutButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  fontWeight: 600,
}));

// ----------------------------------------------------------------------

export default function CartPopover({ isOffset = false }) {
  const dispatch = useDispatch();
  const { translate } = useLocales();
  const { getLocalizedPath } = useLanguage();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Redux state
  const { initial, cartItems } = useSelector((state) => state.shop.cart);

  // Fetch cart on mount
  useEffect(() => {
    if (!initial) {
      dispatch(actionsCart.fetchUserCartItemsRequest());
    }
  }, [dispatch, initial]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const totalItems = cartItems?.length || 0;
  const totalPrice = cartItems?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;

  return (
    <>
      <CartButton isOffset={isOffset} onClick={handleOpen}>
        <StyledBadge badgeContent={totalItems} color="error" max={99}>
          <Iconify icon="solar:cart-3-bold" width={24} height={24} />
        </StyledBadge>
      </CartButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 360,
            maxHeight: 480,
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: (theme) =>
              theme.customShadows?.z24 || `0 24px 48px ${alpha('#000', 0.16)}`,
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {translate('store.cart') || 'Carrito'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalItems} {totalItems === 1 ? 'producto' : 'productos'}
          </Typography>
        </Box>

        <Divider />

        {/* Cart Items */}
        {totalItems === 0 ? (
          <EmptyCartBox>
            <Iconify
              icon="solar:cart-large-minimalistic-bold-duotone"
              width={64}
              height={64}
              sx={{ color: 'text.disabled', mb: 2 }}
            />
            <Typography variant="subtitle1" color="text.secondary">
              {translate('store.empty_cart') || 'Tu carrito está vacío'}
            </Typography>
            <Button
              component={RouterLink}
              to={getLocalizedPath(PATH_PAGE.shop.root)}
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={handleClose}
            >
              {translate('store.keep_shopping') || 'Ir a la tienda'}
            </Button>
          </EmptyCartBox>
        ) : (
          <>
            <Scrollbar sx={{ maxHeight: 280 }}>
              <List disablePadding>
                {cartItems.map((item, index) => (
                  <ListItem
                    key={item.id || index}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemAvatar sx={{ mr: 1.5, minWidth: 'auto' }}>
                      <CartItemImage
                        alt={item.name}
                        src={item.image || '/assets/placeholder.svg'}
                        disabledEffect
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 160,
                          }}
                        >
                          {item.name}
                        </Typography>
                      }
                      secondary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" color="text.secondary">
                            x{item.quantity}
                          </Typography>
                          {item.presentation && (
                            <Typography variant="caption" color="text.disabled">
                              • {item.presentation}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                    <Typography variant="subtitle2" sx={{ ml: 1 }}>
                      {fCentsToCoins(item.price * item.quantity)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Scrollbar>

            <Divider />

            {/* Footer */}
            <Box sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Total</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {fCentsToCoins(totalPrice)}
                </Typography>
              </Stack>

              <CheckoutButton
                component={RouterLink}
                to={getLocalizedPath(PATH_PAGE.shop.checkout)}
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Iconify icon="solar:bag-check-bold" />}
                onClick={handleClose}
              >
                {translate('store.continue') || 'Continuar'}
              </CheckoutButton>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
